// Theme selector + runtime colour-scheme switching.
//
// `overrunBanner` is always Option A (black/white) — the outstanding panel
// stays stark in every scheme. Everything else comes from the *active*
// palette in `tokens.palettes`, read through getters so that components
// which `import { theme }` re-theme automatically on their next render
// after the scheme changes (no per-component refactor needed).
//
// The active scheme is a module-level value swapped synchronously by
// `setActiveScheme` (the store calls it before updating its `colorScheme`
// state, so the re-render that follows already reads the new palette).
//
// See README §7.1 (palette), §7.4 (palette tension).

import { brand, lane, motion, optionA, palettes } from './tokens';
import type { SchemeName } from './tokens';

export type { SchemeName } from './tokens';

export const SCHEME_ORDER: SchemeName[] = [
  'calm',
  'silhouette',
  'distinct',
  'whimsical',
  'master',
  'coastal',
  'harvest',
  'berry',
  'citrus',
  'slate',
  'lavender',
  'forest',
  'rose',
  'ocean',
  'mocha',
  'sunset',
];

export const SCHEME_LABELS: Record<SchemeName, string> = {
  calm: 'Calm',
  silhouette: 'Silhouette',
  distinct: 'Distinct',
  whimsical: 'Whimsical',
  master: 'Master',
  coastal: 'Coastal',
  harvest: 'Harvest',
  berry: 'Berry',
  citrus: 'Citrus',
  slate: 'Slate',
  lavender: 'Lavender',
  forest: 'Forest',
  rose: 'Rose',
  ocean: 'Ocean',
  mocha: 'Mocha',
  sunset: 'Sunset',
};

let active: SchemeName = 'calm';

export function setActiveScheme(name: SchemeName): void {
  if (name in palettes) active = name;
}

export function getActiveScheme(): SchemeName {
  return active;
}

export const theme = {
  get bg() {
    return palettes[active].bg;
  },
  get accent() {
    return palettes[active].accent;
  },
  get text() {
    return palettes[active].text;
  },
  get surface() {
    return palettes[active].surface;
  },
  // Option A wins for the outstanding/overrun panel in every scheme.
  overrunBanner: optionA.overrunBanner,
  // Lane chip colours by course — fixed across schemes.
  lane,
  // Third-party brand colours — fixed across schemes.
  brand,
  motion,
};

export type Theme = typeof theme;
